/**
 * MentionSuggestionMenu - Dropdown menu for @ mentions in TiptapTemplateEditor
 *
 * Shows when user types "@" in text fields that support mentions.
 * Provides contextual options:
 * 1. Link - Opens AddLinkDialog (always available)
 * 2. Flow Variable - Shows flow variables submenu (V2 only, when flowVariables prop provided)
 * 3. Pricing Variable - Shows paywall variables submenu (paywall screens only, existing behavior)
 *
 * V1 safety: When flowVariables prop is not provided, behavior is identical to before.
 *
 * @module reactFlow/components/shared/templateFields/TiptapTemplateEditor/components/MentionSuggestionMenu
 */

import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import {
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Popper,
  ClickAwayListener,
  Box,
  Chip,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import TimerIcon from '@mui/icons-material/Timer';
import CodeIcon from '@mui/icons-material/Code';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import { PAYWALL_VARIABLES, type PaywallVariable } from '../../../../../config/paywallVariablesConfig';
import type { FlowVariableItem } from '../../../../../config/flowVariablesConfig';

/**
 * Props for MentionSuggestionMenu
 */
export interface MentionSuggestionMenuProps {
  /** Anchor element for positioning */
  anchorEl: { getBoundingClientRect: () => DOMRect } | null;

  /** Whether the menu is open */
  open: boolean;

  /** Callback when menu should close */
  onClose: () => void;

  /** Callback when "Link" option is selected */
  onSelectLink: () => void;

  /** Callback when "Countdown" option is selected (optional — only when countdowns are enabled) */
  onSelectCountdown?: () => void;

  /** Callback when a paywall variable is selected (optional — only for Paywall/FlexibleLayout) */
  onSelectVariable?: (variable: PaywallVariable) => void;

  /** Flow variable items (optional — V2 only). When undefined, section is hidden. */
  flowVariables?: FlowVariableItem[];

  /** Callback when a flow variable is selected (optional — V2 only) */
  onSelectFlowVariable?: (item: FlowVariableItem) => void;
}

/**
 * Ref handle for keyboard navigation
 */
export interface MentionSuggestionMenuRef {
  /** Handle keyboard events, returns true if handled */
  onKeyDown: (event: KeyboardEvent) => boolean;
}

const menuPaperSx = {
  width: 300,
  maxHeight: 400,
  overflow: 'auto',
  boxShadow: 3,
  borderRadius: 1,
};

const mainOptionSx = {
  py: 1,
};

const variableItemSx = {
  py: 0.5,
  pl: 4,
};

const accessorItemSx = {
  py: 0.25,
  pl: 6,
};

const variableNameSx = {
  fontFamily: 'monospace',
  color: 'primary.main',
  fontWeight: 500,
  fontSize: '0.875rem',
};

const variableExampleSx = {
  color: 'text.secondary',
  fontSize: '0.75rem',
  ml: 1,
};

const typeBadgeSx = {
  ml: 1,
  height: 18,
  fontSize: '0.65rem',
};

/**
 * Group flow variable items by variableId for hierarchical display
 */
function groupFlowVariables(items: FlowVariableItem[]): Map<string, FlowVariableItem[]> {
  const groups = new Map<string, FlowVariableItem[]>();
  for (const item of items) {
    const group = groups.get(item.variableId) || [];
    group.push(item);
    groups.set(item.variableId, group);
  }
  return groups;
}

/**
 * MentionSuggestionMenu - Shows Link/Variable choice when @ is typed
 */
export const MentionSuggestionMenu = forwardRef<MentionSuggestionMenuRef, MentionSuggestionMenuProps>(
  function MentionSuggestionMenu(
    { anchorEl, open, onClose, onSelectLink, onSelectCountdown, onSelectVariable, flowVariables, onSelectFlowVariable },
    ref,
  ) {
    const [flowVarsExpanded, setFlowVarsExpanded] = useState(false);
    const [paywallVarsExpanded, setPaywallVarsExpanded] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Track if selection is in progress (prevents ClickAwayListener from closing during click)
    const isSelectingRef = useRef(false);

    const hasFlowVariables = flowVariables && flowVariables.length > 0;
    const hasPaywallVariables = !!onSelectVariable && PAYWALL_VARIABLES.length > 0;

    // Group flow variables for hierarchical display
    const flowVariableGroups = useMemo(
      () => (hasFlowVariables ? groupFlowVariables(flowVariables) : new Map()),
      [flowVariables, hasFlowVariables],
    );

    // Reset state when menu opens
    useEffect(() => {
      if (open) {
        setFlowVarsExpanded(false);
        setPaywallVarsExpanded(false);
        setSelectedIndex(0);
        isSelectingRef.current = false;
      }
    }, [open]);

    // Build flat index map for keyboard navigation
    // This maps flat index → action
    const hasCountdowns = !!onSelectCountdown;

    const flatItems = useMemo(() => {
      const items: Array<
        | { type: 'link' }
        | { type: 'countdown' }
        | { type: 'flowVarHeader' }
        | { type: 'flowVarItem'; item: FlowVariableItem }
        | { type: 'paywallVarHeader' }
        | { type: 'paywallVarItem'; item: PaywallVariable }
      > = [];

      // 0: Link
      items.push({ type: 'link' });

      // Countdown (optional — only when handler provided)
      if (hasCountdowns) {
        items.push({ type: 'countdown' });
      }

      // Flow variables section
      if (hasFlowVariables) {
        items.push({ type: 'flowVarHeader' });
        if (flowVarsExpanded) {
          for (const item of flowVariables) {
            items.push({ type: 'flowVarItem', item });
          }
        }
      }

      // Paywall variables section (existing)
      if (hasPaywallVariables) {
        items.push({ type: 'paywallVarHeader' });
        if (paywallVarsExpanded) {
          for (const pv of PAYWALL_VARIABLES) {
            items.push({ type: 'paywallVarItem', item: pv });
          }
        }
      }

      return items;
    }, [hasCountdowns, hasFlowVariables, hasPaywallVariables, flowVarsExpanded, paywallVarsExpanded, flowVariables]);

    // Handle item selection by index
    const handleSelect = useCallback(
      (index: number) => {
        const entry = flatItems[index];
        if (!entry) return;

        switch (entry.type) {
          case 'link':
            isSelectingRef.current = true;
            onSelectLink();
            requestAnimationFrame(() => { isSelectingRef.current = false; });
            break;
          case 'countdown':
            isSelectingRef.current = true;
            onSelectCountdown?.();
            requestAnimationFrame(() => { isSelectingRef.current = false; });
            break;
          case 'flowVarHeader':
            setFlowVarsExpanded((prev) => !prev);
            break;
          case 'flowVarItem':
            isSelectingRef.current = true;
            onSelectFlowVariable?.(entry.item);
            requestAnimationFrame(() => { isSelectingRef.current = false; });
            break;
          case 'paywallVarHeader':
            setPaywallVarsExpanded((prev) => !prev);
            break;
          case 'paywallVarItem':
            isSelectingRef.current = true;
            onSelectVariable?.(entry.item);
            requestAnimationFrame(() => { isSelectingRef.current = false; });
            break;
        }
      },
      [flatItems, onSelectLink, onSelectCountdown, onSelectFlowVariable, onSelectVariable],
    );

    // Wrapper for onClose that respects selection guard
    const handleClickAway = useCallback(() => {
      if (!isSelectingRef.current) {
        onClose();
      }
    }, [onClose]);

    // Expose keyboard navigation
    useImperativeHandle(
      ref,
      () => ({
        onKeyDown: (event: KeyboardEvent) => {
          if (!open) return false;

          switch (event.key) {
            case 'ArrowDown':
              event.preventDefault();
              setSelectedIndex((prev) => Math.min(prev + 1, flatItems.length - 1));
              return true;

            case 'ArrowUp':
              event.preventDefault();
              setSelectedIndex((prev) => Math.max(prev - 1, 0));
              return true;

            case 'Enter':
              event.preventDefault();
              handleSelect(selectedIndex);
              return true;

            case 'Escape':
              event.preventDefault();
              onClose();
              return true;

            case 'Tab':
              onClose();
              return false;

            default:
              return false;
          }
        },
      }),
      [open, selectedIndex, flatItems, handleSelect, onClose],
    );

    if (!open || !anchorEl) {
      return null;
    }

    // Track current flat index for rendering
    let currentFlatIndex = 0;

    return (
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: 1400 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 4],
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper sx={menuPaperSx} data-mention-menu data-testid="mention-suggestion-menu">
            <List dense disablePadding>
              {/* Link option */}
              {(() => {
                const idx = currentFlatIndex++;
                return (
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={selectedIndex === idx}
                      onMouseDown={() => { isSelectingRef.current = true; }}
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedIndex(idx);
                        onSelectLink();
                        requestAnimationFrame(() => { isSelectingRef.current = false; });
                      }}
                      sx={mainOptionSx}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <LinkIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Link"
                        secondary="Add a hyperlink or screen reference"
                        slotProps={{
                          secondary: { variant: 'caption' },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })()}

              {/* Countdown option (when countdowns are enabled) */}
              {hasCountdowns && (() => {
                const idx = currentFlatIndex++;
                return (
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={selectedIndex === idx}
                      onMouseDown={() => { isSelectingRef.current = true; }}
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedIndex(idx);
                        onSelectCountdown?.();
                        requestAnimationFrame(() => { isSelectingRef.current = false; });
                      }}
                      sx={mainOptionSx}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <TimerIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Countdown"
                        secondary="Add an inline countdown timer"
                        slotProps={{
                          secondary: { variant: 'caption' },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })()}

              {/* Flow Variables section (V2 only) */}
              {hasFlowVariables && (() => {
                const headerIdx = currentFlatIndex++;
                return (
                  <>
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={selectedIndex === headerIdx}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedIndex(headerIdx);
                          setFlowVarsExpanded((prev) => !prev);
                        }}
                        sx={mainOptionSx}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <DataObjectIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Variable"
                          secondary="Insert flow variable reference"
                          slotProps={{
                            secondary: { variant: 'caption' },
                          }}
                        />
                        {flowVarsExpanded ? <ExpandLess /> : <ExpandMore />}
                      </ListItemButton>
                    </ListItem>

                    <Collapse in={flowVarsExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {flowVariables.map((fv) => {
                          const itemIdx = currentFlatIndex++;
                          return (
                            <ListItem key={fv.ref} disablePadding>
                              <ListItemButton
                                selected={selectedIndex === itemIdx}
                                onMouseDown={() => { isSelectingRef.current = true; }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setSelectedIndex(itemIdx);
                                  onSelectFlowVariable?.(fv);
                                  requestAnimationFrame(() => { isSelectingRef.current = false; });
                                }}
                                sx={fv.isAccessor ? accessorItemSx : variableItemSx}
                              >
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography component="span" sx={variableNameSx}>
                                        {fv.displayLabel}
                                      </Typography>
                                      {!fv.isAccessor && (
                                        <Chip label={fv.type} size="small" variant="outlined" sx={typeBadgeSx} />
                                      )}
                                    </Box>
                                  }
                                  secondary={fv.description}
                                  slotProps={{
                                    secondary: {
                                      variant: 'caption',
                                      sx: { display: 'block', mt: 0.25 },
                                    },
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </>
                );
              })()}

              {/* Paywall Variables section (existing behavior) */}
              {hasPaywallVariables && (() => {
                const headerIdx = currentFlatIndex++;
                return (
                  <>
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={selectedIndex === headerIdx}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedIndex(headerIdx);
                          setPaywallVarsExpanded((prev) => !prev);
                        }}
                        sx={mainOptionSx}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CodeIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={hasFlowVariables ? 'Pricing Variable' : 'Variable'}
                          secondary="Insert pricing variable (@price, @duration...)"
                          slotProps={{
                            secondary: { variant: 'caption' },
                          }}
                        />
                        {paywallVarsExpanded ? <ExpandLess /> : <ExpandMore />}
                      </ListItemButton>
                    </ListItem>

                    <Collapse in={paywallVarsExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {PAYWALL_VARIABLES.map((variable) => {
                          const itemIdx = currentFlatIndex++;
                          return (
                            <ListItem key={variable.name} disablePadding>
                              <ListItemButton
                                selected={selectedIndex === itemIdx}
                                onMouseDown={() => { isSelectingRef.current = true; }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setSelectedIndex(itemIdx);
                                  onSelectVariable(variable);
                                  requestAnimationFrame(() => { isSelectingRef.current = false; });
                                }}
                                sx={variableItemSx}
                              >
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography component="span" sx={variableNameSx}>
                                        @{variable.name}
                                      </Typography>
                                      <Typography component="span" sx={variableExampleSx}>
                                        {variable.example}
                                      </Typography>
                                    </Box>
                                  }
                                  secondary={variable.description}
                                  slotProps={{
                                    secondary: {
                                      variant: 'caption',
                                      sx: { display: 'block', mt: 0.25 },
                                    },
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </>
                );
              })()}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>
    );
  },
);

export default MentionSuggestionMenu;
